import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const Privacy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | BetterByAI</title>
        <meta name="description" content="Privacy Policy for BetterByAI - Learn about how we collect, use, and protect your data." />
        <link rel="canonical" href="https://betterbyai.com/privacy" />
      </Helmet>

      <div className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/50 backdrop-blur-sm rounded-3xl border border-[#FF7066]/10 p-8 sm:p-10 shadow-lg"
          >
            <h1 className="text-4xl font-bold text-primary mb-8">Privacy Policy</h1>
            
            <div className="prose prose-lg max-w-none text-secondary">
              <p className="text-lg mb-6">
                Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Introduction</h2>
              <p>
                At BetterByAI, we respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you visit our website or use our services.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Information We Collect</h2>
              <p>We may collect several types of information, including:</p>
              <ul className="list-disc pl-6 mb-6">
                <li><strong>Personal identification information</strong> (Name, email address, phone number, etc.) when you contact us or sign up for our services</li>
                <li><strong>Usage data</strong> (IP address, browser type, pages visited, time spent, etc.)</li>
                <li><strong>Cookies and tracking technologies</strong> data as detailed below</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">How We Use Cookies</h2>
              <p>
                Cookies are small files that a site or its service provider transfers to your computer's hard drive through your web browser that enables the site to recognise your browser and capture and remember certain information.
              </p>
              <p className="mt-4">We use cookies to:</p>
              <ul className="list-disc pl-6 mb-6">
                <li>Understand and save user preferences</li>
                <li>Compile aggregate data about site traffic and interactions</li>
                <li>Improve our website and provide better service</li>
                <li>Assist in our marketing and advertising efforts</li>
              </ul>
              <p className="mt-4">
                You can choose to have your computer warn you each time a cookie is being sent, or you can choose to turn off all cookies through your browser settings. Each browser is a little different, so look at your browser's Help menu to learn the correct way to modify your cookies.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Third-Party Services</h2>
              <p>
                We may use third-party service providers to help us operate our website or administer activities on our behalf, such as sending out newsletters or surveys. We may share your information with these third parties for those limited purposes.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Data Security</h2>
              <p>
                We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Your Data Protection Rights</h2>
              <p>
                Depending on your location, you may have the following rights regarding your personal data:
              </p>
              <ul className="list-disc pl-6 mb-6">
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate information</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restriction of processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-[#FF7066]/5 rounded-lg border border-[#FF7066]/10">
                <p><strong>Email:</strong> <a href="mailto:amrit@betterbyai.com" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors">amrit@betterbyai.com</a></p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Privacy;